import usePersonalPresetsEnabled from '@src/hooks/usePersonalPresetsEnabled';
import { APPLY_PRESET_VIEW } from '@src/utils/presets/personalPresets';
import { dimensionMetricsSelector, globalOrderBySelector } from '@src/store/trends/base/selectors';
import { getSecondaryMetric } from '@src/utils/metrics/metricHelpers';
import { dimensionSortsAtom, tableMetricsAtom } from '@src/components/dimension-panel-hz/atoms';
import React, { useState, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useSetAtom, useAtom } from 'jotai';
import { metricSelector, selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import MetricSelectorPopup from '@components/common/MetricSelectorPopup';
import { setMetrics } from '@store/metricsChartContainer/actions';
import { preserveOrder } from '@components/common/DnD/utils';
import { supportedMetricsSelector } from '@store/common/selectors';
import useSupportedMetrics from '@hooks/metrics/useSupportedMetrics';
import useSupportedFlowMetrics from '@hooks/metrics/useSupportedFlowMetrics';
import useGlobalExcludeMetricCategories from '@src/hooks/metrics/useGlobalExcludeMetricCategories';
import { refreshTrends } from '@store/common/actions';
import { diagnosticsModeAtom } from '../atoms';
import { isAppModule } from '@utils/constants';
import { useCurrentLimitation } from '@src/hooks/useCurrentLimitation';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { currentKpiIdAtom } from '@src/atoms/panels';

const MetricsPopup = () => {
  const dispatch = useDispatch();
  const allowDimensionApply = usePersonalPresetsEnabled();
  const dimensionMetrics = useSelector(dimensionMetricsSelector) as string[];
  const sortMetric = useSelector(globalOrderBySelector);
  const setDimensionSorts = useSetAtom(dimensionSortsAtom);
  const setTableMetrics = useSetAtom(tableMetricsAtom);
  const selectedMetricsFromStore = useSelector(metricSelector);
  const selectedTrendMetric = useSelector(selectedMetricSelector);
  const supportedMetrics: Record<string, any> = useSelector(supportedMetricsSelector);
  const setDiagnosticsMode = useSetAtom(diagnosticsModeAtom);
  const [currentKpiId, setCurrentKpiId] = useAtom(currentKpiIdAtom);
  const [selectedMetrics, setSelectedMetrics] = useState(selectedMetricsFromStore);
  const metricCountLimit = isAppModule ? 20 : 999;
  const { flowId } = useDrilldownMeta();
  const globalExcludeMetricsCategory = useGlobalExcludeMetricCategories([]);
  const limitation = useCurrentLimitation();

  const metrics = useSupportedMetrics({
    excludeCategories: globalExcludeMetricsCategory,
    limitation,
    allMetrics: supportedMetrics,
  });

  const flowData = useSupportedFlowMetrics({
    allMetrics: supportedMetrics,
    flowId,
  });

  useEffect(() => {
    setSelectedMetrics(selectedMetricsFromStore);
  }, [selectedMetricsFromStore]);

  const onApply = (metrics, kpiChanged, applyToDimensions = false) => {
    if (allowDimensionApply) {
      const ordered = preserveOrder(selectedMetricsFromStore, metrics);
      const focused = ordered.includes(selectedTrendMetric) ? selectedTrendMetric : ordered[0];
      const secondary = getSecondaryMetric({ metricKey: focused, defaultValue: focused });
      // Keep normal focus dependencies when only changing the metric panel.
      const columns = applyToDimensions
        ? ordered
        : focused === selectedTrendMetric
          ? dimensionMetrics
          : Array.from(
              new Set([
                ...dimensionMetrics
                  .filter((metric) => metric !== focused && metric !== secondary)
                  .slice(0, 18),
                secondary,
                focused,
              ]),
            );
      const orderBy = columns.includes(sortMetric) ? sortMetric : columns[0];
      batch(() => {
        dispatch({
          type: APPLY_PRESET_VIEW,
          view: {
            metricsChartContainer: { metric: focused, metrics: ordered },
            base: { dimensionMetrics: columns, globalOrderBy: orderBy },
          },
        });
        setTableMetrics({ metrics: [...columns].sort(), orderBy });
        if (applyToDimensions)
          setDimensionSorts((sorts) =>
            Object.fromEntries(
              Object.entries(sorts).filter(
                ([dimension, sort]) => sort.key === dimension || ordered.includes(sort.key),
              ),
            ),
          );
      });
    } else {
      dispatch(setMetrics(preserveOrder(selectedMetricsFromStore, metrics)));
    }

    // refresh data on page
    if (kpiChanged) {
      dispatch(refreshTrends());
    }
    // disable diagnostics mode when metric changes are applied
    setDiagnosticsMode(false);
  };

  return (
    <MetricSelectorPopup
      allowDimensionApply={allowDimensionApply}
      supportedMetrics={metrics}
      flowData={flowData}
      selectedMetrics={selectedMetrics}
      lockedMetrics={[selectedTrendMetric]}
      onApply={onApply}
      metricsCountLimit={metricCountLimit}
      config={limitation?.metrics?.config}
      currentKpiId={currentKpiId}
      onKpiChange={setCurrentKpiId}
    />
  );
};

export default React.memo(MetricsPopup);
